import { useSearch } from "@tanstack/react-router";
import { LayoutGrid, Network } from "lucide-react";
import { useState } from "react";
import { OrganizationTree } from "@/components/organization/OrganizationTree";
import { OrgBoard } from "@/components/organization/OrgBoard";
import { BackLink } from "@/components/shared/back-link";
import { useSetBreadcrumbs } from "@/components/shared/breadcrumbs";
import { PageHeader } from "@/components/shared/page-header";
import { originTarget, parseOriginSearch } from "@/lib/detail-origin";
import { cn } from "@/lib/utils";

type OrgView = "structure" | "board";

const VIEWS: { value: OrgView; label: string; icon: typeof Network }[] = [
  { value: "structure", label: "Structure", icon: Network },
  { value: "board", label: "Board", icon: LayoutGrid },
];

export function OrganizationPage() {
  const [view, setView] = useState<OrgView>("structure");

  // Arrived from a specific record (e.g. a person's Department / Role Team)?
  // Return there. Frozen at mount — the Board view rewrites this route's query
  // string (filters) via replaceState, which would otherwise drop the origin.
  const search = useSearch({ strict: false });
  const [origin] = useState(() => originTarget(parseOriginSearch(search)));
  useSetBreadcrumbs(
    origin
      ? [origin, { label: "Organization" }]
      : [{ label: "People & Organization" }, { label: "Organization" }],
  );

  return (
    <div className="space-y-6">
      {origin && (
        <BackLink
          to={origin.to}
          params={origin.params}
          search={origin.search}
          label={origin.label}
        />
      )}

      <div className="flex flex-wrap items-start justify-between gap-4">
        <PageHeader
          title="Organization"
          description={
            view === "structure"
              ? "How the company is organized — pillars, their departments, and the role teams within each."
              : "Everyone placed on a role team, grouped by pillar and department and accented by employment type."
          }
        />
        <div className="inline-flex rounded-lg border bg-muted/40 p-0.5">
          {VIEWS.map(({ value, label, icon: Icon }) => (
            <button
              key={value}
              type="button"
              aria-pressed={view === value}
              onClick={() => setView(value)}
              className={cn(
                "inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 font-medium text-sm transition-colors",
                view === value
                  ? "bg-background text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              <Icon className="h-4 w-4" />
              {label}
            </button>
          ))}
        </div>
      </div>

      {view === "structure" ? <OrganizationTree /> : <OrgBoard />}
    </div>
  );
}
